import styled from 'styled-components';
import { ProjectMetricsDto } from 'api/models';
import { formatCurrency, formatSquareMeters } from 'utility/utility';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`;

const ProjectCard = styled.div`
  padding: 20px;
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  transition: border-color 0.2s;

  &:hover {
    border-color: #bfdbfe;
  }
`;

const ProjectName = styled.h4`
  font-weight: 600;
  color: #1f2937;
`;

const MetricsContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MetricLabel = styled.span`
  font-size: 14px;
  color: #4b5563;
`;

const MetricValue = styled.span<{ variant?: 'default' | 'success' }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => (props.variant === 'success' ? '#059669' : '#1f2937')};
`;

const ProgressSection = styled.div`
  margin-top: 16px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const ProgressLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const ProgressValue = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #374151;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ width: number }>`
  height: 100%;
  background: #3b82f6;
  border-radius: 9999px;
  transition: width 0.5s;
  width: ${props => props.width}%;
`;

export const ProjectMetrics = ({ data }: { data: ProjectMetricsDto[] }) => {
  const { t } = useTranslation();

  return (
    <Container>
      <Title>{t('project-metrics:title', 'Project Metrics')}</Title>
      <Grid>
        {data.map((project: ProjectMetricsDto) => (
          <ProjectCard key={project.projectId}>
            <ProjectName>{project.projectName}</ProjectName>
            <MetricsContainer>
              <MetricRow>
                <MetricLabel>{t('project-metrics:square-meters', 'Square Meters')}</MetricLabel>
                <MetricValue>{formatSquareMeters(project?.totalSquareMeters || 0)}</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>{t('project-metrics:available', 'Available')}</MetricLabel>
                <MetricValue>{formatSquareMeters(project?.freeSquareMeters || 0)}</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>{t('project-metrics:revenue', 'Revenue')}</MetricLabel>
                <MetricValue variant="success">{formatCurrency(project?.totalRevenue || 0)}</MetricValue>
              </MetricRow>
              <ProgressSection>
                <ProgressHeader>
                  <ProgressLabel>{t('project-metrics:sales-progress', 'Sales Progress')}</ProgressLabel>
                  <ProgressValue>
                    {Math.round(((project?.soldSquareMeters || 0) / (project?.totalSquareMeters || 1)) * 100)}%
                  </ProgressValue>
                </ProgressHeader>
                <ProgressBar>
                  <ProgressFill width={((project?.soldSquareMeters || 0) / (project?.totalSquareMeters || 1)) * 100} />
                </ProgressBar>
              </ProgressSection>
            </MetricsContainer>
          </ProjectCard>
        ))}
      </Grid>
    </Container>
  );
};